import { Plus } from "lucide-react";
import React, { useContext, useEffect } from "react";
import { useCookies } from "react-cookie";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import tips from "../../../../assets/copy/tips";
import Loader from "../../../../components/Loader";
import EducationSummaryBox from "./../../../../components/ResumeBuilder/EducationSummaryBox/index";
import Tips from "../../../../components/Tips";
import { Button, buttonVariants } from "../../../../components/ui/button";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import { NextButton, resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_ACTIONS, RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import type { ResumeContextValue } from "../../../../types/resume";

const ResumeEducationSummary = () => {
	const navigate = useNavigate();

	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	let { education, workHistory, journal } = resumeData;
	let skipOne = 0;

	let resumeCurrentStatus = journal ? journal.currentStatus : false;
	let isComplete = resumeCurrentStatus == "COMPLETED";

	const [loading, setLoading] = React.useState(true);
	useEffect(() => {
		if (resumeData.contactInfo.firstName === "") {
			return setLoading(true);
		}
		return setLoading(false);
	}, [resumeData]);

	// if (yearsOfExperience === "No Experience") {
	skipOne = workHistory.length > 0 ? 2 : 0;
	// }

	const nextTxt = NextButton(isComplete, "Work Details", navigationHandler, skipOne);

	function next(e) {
		e.preventDefault();

		let nextLink = navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete, skipOne).value
			.url;

		if (nextLink === "review") return navigate("/my-resumes");
		return navigate(`/resume/${nextLink}`);
	}

	useEffect(() => {}, [resumeData]);

	const addAnotherEduHandler = () => {
		navigationHandler.getPrevious(navigationHandler.template.navigationFlow, link, isComplete, 0);

		return navigate("/resume/education-information");
	};

	function onSuccess(response) {
		resumeObjectToContextFormat(response, setResumeData);
	}

	function onError() {
		toast.error("Something went wrong");
	}
	const [cookies] = useCookies(["resume"]);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const resumeId = cookies[staticValues.localStorageResumeId];

	const patchSection = usePatchResumeSection();

	const deleteHandler = (eduId) => {
		patchSection.mutate(
			{
				resumeId,
				section: RESUME_SECTIONS.EDUCATION,
				data: { referenceId: eduId },
				action: RESUME_ACTIONS.DELETE,
			} as never,
			{ onSuccess, onError },
		);
	};

	useEffect(() => {
		if (!loading && education.length === 0) {
			navigate("/resume/education-information");
		}
	}, [education]);

	const summaryBox = education.map((summaryItem, index) => (
		<EducationSummaryBox
			item={summaryItem}
			deleteEduSummary={deleteHandler}
			key={index}
			index={summaryItem.referenceId}
		/>
	));

	useEffect(() => {}, [resumeData]);

	return (
		<div className="min-h-screen">
			<div className="md:w-2/3">
				<div className="mb-2 flex justify-end">
					<Tips content={tips.educationHistory} />
				</div>
			</div>

			{loading ? (
				<Loader />
			) : (
				<div className="md:w-2/3">
					{summaryBox}
					<Button type="button" variant="link" className="font-semibold cursor-pointer" onClick={addAnotherEduHandler}>
						<Plus className="mr-3 size-4" />
						Add another education
					</Button>
					<div className="mt-4 flex items-center justify-end">
						<Link to="/resume/skills" className={buttonVariants()} onClick={next}>
							{nextTxt}
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};

export default ResumeEducationSummary;
